import { useState, useEffect, useMemo, useCallback } from 'react';
import { getFacility } from '../api/viewFacilityApiService';
import {
  getStatusLabel,
  getStatusBadgeClasses,
} from '@/shared/utils/statusBadge';
import type { ViewFacilityDetail } from '../types/viewFacilityTypes';
import { FACILITY_LABELS } from '../constants/facilityLabels';

// ─── Nav items ────────────────────────────────────────────────────────────────

export const FACILITY_PROFILE_NAV_ITEMS = [
  { id: 'BasicInformation', label: FACILITY_LABELS.VIEW_NAV_BASIC },
  { id: 'Equipment', label: FACILITY_LABELS.SECTION_EQUIPMENT },
  { id: 'Users', label: 'Pracownicy' },
];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseViewFacilityReturn {
  facility: ViewFacilityDetail | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };
  refetch: () => Promise<void>;
}

export const useViewFacility = (facilityId: string): UseViewFacilityReturn => {
  const [facility, setFacility] = useState<ViewFacilityDetail | undefined>(
    undefined
  );
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchFacility = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getFacility(facilityId);
      if (result.success && result.data) {
        setFacility(result.data.facility);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
      }
    } catch {
      setShowErrorScreen(true);
    } finally {
      setIsLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    void getFacility(facilityId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.success && result.data) {
          setFacility(result.data.facility);
          setShowErrorScreen(false);
        } else {
          setShowErrorScreen(true);
        }
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setShowErrorScreen(true);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  const statusLabel = useMemo(
    () => getStatusLabel(facility?.status),
    [facility?.status]
  );

  const statusBadgeClasses = useMemo(
    () => getStatusBadgeClasses(facility?.status),
    [facility?.status]
  );

  return {
    facility,
    isLoading,
    showErrorScreen,
    statusLabel,
    statusBadgeClasses,
    refetch: fetchFacility,
  };
};
